import Close from "@mui/icons-material/Close";
import { Alert, Box, LinearProgress } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { FormEvent, useCallback, useState } from "react";
import { useUpdatePatientAddressMutation } from "src/api/services/addressApi";
import { useValidatePlaceQuery } from "src/api/services/placesApi";
import { ShippingAddress } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import PlacesAutocomplete from "src/components/PlacesAutocomplete";
import { isError } from "src/utils/isError";
import { desktopSx } from "src/utils/styles/modalStyles";
import AddressDetails from "./components/AddressDetails";

interface Props {
  open: boolean;
  onClose: () => void;
  address: ShippingAddress;
}

const UpdateAddressModal = ({ open, onClose, address }: Props) => {
  const [error, setError] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [placeId, setPlaceId] = useState<string | null>(null);
  const [updateAddress, { isLoading }] = useUpdatePatientAddressMutation();

  const {
    currentData: addressDetails,
    isFetching,
    error: errorFetching,
  } = useValidatePlaceQuery(placeId ?? skipToken);

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setError("");

      if (addressDetails == null) return;

      updateAddress({
        patient_id: address.patient_id,
        address_line1: addressDetails.address.streetAddress,
        address_line2: addressLine2,
        city: addressDetails.address.city,
        state: addressDetails.address.state,
        zip_code: addressDetails.address.ZIPCode,
      })
        .unwrap()
        .catch((err) => {
          if (isError(err)) {
            setError(err.data.message);
          }
        })
        .then(() => {
          onClose();
          setPlaceId(null);
          setAddressLine2("");
        });
    },
    [address.patient_id, addressDetails, addressLine2, onClose, updateAddress],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={{ ...desktopSx, minHeight: 500 }}
        gap={3}
        component="form"
        onSubmit={onSubmit}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography fontWeight={700} variant="h5">
            Update address:
          </Typography>
          <IconButton onClick={onClose}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Stack gap={2} overflow="scroll" maxHeight={600}>
          <Typography fontWeight={600}>Search address:</Typography>
          <Alert severity="info" sx={{ width: 400 }}>
            For best results, please avoid abbreviations (e.g.,{" "}
            <em>NE, NW, ST</em>) and use the full street or location names
            instead.
          </Alert>
          <PlacesAutocomplete onSelect={setPlaceId} />
          <Stack gap={2}>
            <Box height={4}>
              {isFetching ? <LinearProgress color="secondary" /> : null}
            </Box>
            {addressDetails ? (
              <AddressDetails
                addressDetails={addressDetails}
                addressLine2={addressLine2}
                setAddressLine2={setAddressLine2}
              />
            ) : errorFetching != null ? (
              <Typography sx={{ color: "red" }} textAlign="center">
                {isError(errorFetching)
                  ? errorFetching.data.message
                  : "Could not verify address"}
              </Typography>
            ) : (
              <Typography textAlign="center">0 results found</Typography>
            )}
          </Stack>
        </Stack>

        <Stack gap={1}>
          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error}
            </Typography>
          ) : null}
          <LoadingButton
            loading={isLoading}
            type="submit"
            disabled={addressDetails == null}
          >
            Confirm
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default UpdateAddressModal;
